import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormArray, FormControl, FormBuilder, Validators, ValidatorFn } from '@angular/forms';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { MatSelectChange } from '@angular/material/select';
import { MatRadioChange } from '@angular/material/radio';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { AppConst } from '../../app-const';
import { SpinnerService } from '../../services/spinner.service';
import { ApiReservation, ApiSetReservationsRequest } from '../../api-client-module';
import { ReservationFacility } from '../../firestore-models/reservation-facility';
import { ReservationQuestion } from '../../firestore-models/reservation-question';
import { ReservationFacilityAttribute } from '../../firestore-models/reservation-facility-attribute';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';

import { ReservationCheckService, isAnyChecked } from './reservation-check.service';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface ReservationDialogData {
  change_type: number;
  reservation?: ApiReservation;
  reservationFacility?: ReservationFacility | null;
}

@Component({
  selector: 'app-reservation-dialog',
  templateUrl: './reservation-dialog.component.html',
  styleUrls: ['./reservation-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class ReservationDialogComponent implements OnInit {

  //予約情報
  reservation?: ApiReservation;

  applyForm: FormGroup;
  get options(): FormArray {
    return this.applyForm.get('options') as FormArray;
  }
  questions: Array<any> = [];

  projectId: string;

  projectLanguages!: Array<ProjectLanguage>;

  //予約情報フォーム
  reservationFormGroup!: FormGroup;
  reservationFacilityIdControl!: FormControl;
  reservationFacilityAttributeIdControl!: FormControl;
  reservationNoControl!: FormControl;
  reservationStatusControl!: FormControl;
  emailAddressControl!: FormControl;
  companyNameControl!: FormControl;
  departmentControl!: FormControl;
  jobTitleControl!: FormControl;
  surnameChineseControl!: FormControl;
  nameChineseControl!: FormControl;
  familyNameControl!: FormControl;
  firstNameControl!: FormControl;
  birthdayControl!: FormControl;
  genderControl!: FormControl;
  postalCodeControl!: FormControl;
  prefectureControl!: FormControl;
  municipalitiesControl!: FormControl;
  streetAddressControl!: FormControl;
  nameAndNumberBuildingControl!: FormControl;
  mobilePhoneNumberControl!: FormControl;
  userRequestControl!: FormControl;
  memoControl!: FormControl;
  mailSendStatusControl!: FormControl;

  inReservationFacilityId = '';
  reservationUserInputTitle = '';

  isVisibledAddress = true;

  reservationStatuses = AppConst.RESERVATION_STATUSES;
  mailSendStatuses = AppConst.MAIL_SEND_STATUSES;
  genders = [''];

  RESERVATION_STATUS_RECEPTION = AppConst.RESERVATION_STATUS_RECEPTION;
  RESERVATION_STATUS_COMPLETE = AppConst.RESERVATION_STATUS_COMPLETE;
  RESERVATION_STATUS_CANCEL = AppConst.RESERVATION_STATUS_CANCEL;
  RESERVATION_STATUS_LOST = AppConst.RESERVATION_STATUS_LOST;

  private reservationFacilitiesCollection!: AngularFirestoreCollection<ReservationFacility>;
  reservationFacilitiesObservable!: Observable<ReservationFacility[]>;
  private reservationFacilityAttributesCollection!: AngularFirestoreCollection<ReservationFacilityAttribute>;
  reservationFacilityAttributes!: Observable<ReservationFacilityAttribute[]>;
  reservationFacilitiesSubscribe!: Subscription;
  reservationFacilities!: Array<ReservationFacility>;
  reservationFacility!: ReservationFacility;

  projectBaseLang: string = 'ja';
  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  constructor(
    public dialogRef: MatDialogRef<ReservationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReservationDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private reservationCheckService: ReservationCheckService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.applyForm = this.formBuilder.group({
      options: this.formBuilder.array([]),
    }, {
      validator: []
    });
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.genders = [...this.genders, ...AppConst.GENDERS];
    this.reservationFacilitiesCollection = this.angularFirestore.collection<ReservationFacility>(
      AppConst.FIRESTORE_RESERVATION_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('reservation_facility_id', 'asc'));
    this.reservationFacilitiesObservable = this.reservationFacilitiesCollection.valueChanges({ idField: 'id' });
    this.reservationFacilitiesSubscribe = this.reservationFacilitiesObservable.subscribe(reservationFacilities => {
      this.reservationFacilities = reservationFacilities;
    });    
    this.reservationFacilityAttributesCollection = this.angularFirestore.collection<ReservationFacilityAttribute>(
      AppConst.FIRESTORE_RESERVATION_FACILITY_ATTR, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.reservationFacilityAttributes = this.reservationFacilityAttributesCollection.valueChanges({ idField: 'id' });
    this.reservation = this.data.reservation;
    if (this.data.reservationFacility) {
      this.reservationFacility = this.data.reservationFacility;
      this.questions = this.data.reservationFacility.questions;
    }
    if (!this.reservation) {
      this.reservation = {
        project_id: this.projectId,
        facility_id: '',
        exhibit_id: '',
        reservation_facility_id: '',
        reservation_facility_attribute_id: '',
        reservation_no: '',
        reservation_status: AppConst.RESERVATION_STATUS_RECEPTION,
        reservation_start_date: null as any,
        reservation_end_date: null as any,
        reservation_times: [],
        reservation_cancel_date: null as any,
        reservation_cancel_user: '',
        email_address: '',
        company_name: '',
        department: '',
        job_title: '',
        surname_chinese: '',
        name_chinese: '',
        family_name: '',
        first_name: '',
        birthday: null as any,
        gender: '',
        postal_code: '',
        prefecture: '',
        municipalities: '',
        street_address: '',
        name_and_number_building: '',
        mobile_phone_number: '',
        user_request: '',
        memo: '',
        mail_send_status: AppConst.MAIL_SEND_STATUS_UNSEND,
        notification_mode: AppConst.MAIL_NOTIFICATION_OFF,
        answer: [],
        preview: true,
        user_site_url: '',
        splats_qr_code: '',
        splats_guest_id: '',
        enabled: true,
        created: null as any,
        updated: null as any,
      };
    }
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });
    
    // カスタム項目の設定がある場合はカスタム項目用のフォームを生成
    if (this.reservationFacility.questions 
     && this.reservationFacility.questions.length > 0
    ) {
      this.questions = this.reservationFacility.questions;
      this.addOptionForm(this.questions);
    }
    this.createForm();
    this.reservationFacilityIdControl = this.reservationFormGroup.get('reservationFacilityId') as FormControl;
    this.reservationFacilityAttributeIdControl = this.reservationFormGroup.get('reservationFacilityAttributeId') as FormControl;
    this.reservationNoControl = this.reservationFormGroup.get('reservationNo') as FormControl;
    this.reservationStatusControl = this.reservationFormGroup.get('reservationStatus') as FormControl;
    this.emailAddressControl = this.reservationFormGroup.get('emailAddress') as FormControl;
    this.companyNameControl = this.reservationFormGroup.get('companyName') as FormControl;
    this.departmentControl = this.reservationFormGroup.get('department') as FormControl;
    this.jobTitleControl = this.reservationFormGroup.get('jobTitle') as FormControl;
    this.surnameChineseControl = this.reservationFormGroup.get('surnameChinese') as FormControl;
    this.nameChineseControl = this.reservationFormGroup.get('nameChinese') as FormControl;
    this.familyNameControl = this.reservationFormGroup.get('familyName') as FormControl;
    this.firstNameControl = this.reservationFormGroup.get('firstName') as FormControl;
    this.birthdayControl = this.reservationFormGroup.get('birthday') as FormControl;
    this.genderControl = this.reservationFormGroup.get('gender') as FormControl;
    this.postalCodeControl = this.reservationFormGroup.get('postalCode') as FormControl;
    this.prefectureControl = this.reservationFormGroup.get('prefecture') as FormControl;
    this.municipalitiesControl = this.reservationFormGroup.get('municipalities') as FormControl;
    this.streetAddressControl = this.reservationFormGroup.get('streetAddress') as FormControl;
    this.nameAndNumberBuildingControl = this.reservationFormGroup.get('nameAndNumberBuilding') as FormControl;
    this.mobilePhoneNumberControl = this.reservationFormGroup.get('mobilePhoneNumber') as FormControl;
    this.userRequestControl = this.reservationFormGroup.get('userRequest') as FormControl;
    this.memoControl = this.reservationFormGroup.get('memo') as FormControl;
    this.mailSendStatusControl = this.reservationFormGroup.get('mailSendStatus') as FormControl;
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  //以下、フォームの記入エラー表示
  // フォームの記入エラー表示部分
  public getErrorMessageToReservationStatus() {
    return this.reservationStatusControl.hasError('required')
      ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToEmailAddress() {
    return this.emailAddressControl.hasError('required')
      ? this.translate.instant('必須項目')
    : this.emailAddressControl.hasError('pattern')
          ? this.translate.instant('メールアドレス形式で入力')
          : '';
  }

  public getErrorMessageToFamilyName() {
    return this.familyNameControl.hasError('required')
      ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToFirstName() {
    return this.firstNameControl.hasError('required')
      ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToGender() {
    return this.genderControl.hasError('required')
      ? this.translate.instant('必須項目')
    : '';
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.reservationFormGroup = this.formBuilder.group(
      {
        reservationFacilityId: [this.reservation!.reservation_facility_id, [Validators.required, Validators.pattern('^[a-zA-Z0-9-]+$')]],
        reservationFacilityAttributeId: [this.reservation!.reservation_facility_attribute_id],
        reservationNo: [this.reservation!.reservation_no, [Validators.required]],
        reservationStatus: [this.reservation!.reservation_status, [Validators.required]],
        emailAddress: [this.reservation!.email_address, [Validators.required, Validators.pattern('^[a-zA-Z0-9_+-]+(.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$')]],
        companyName: [this.reservation!.company_name, []],
        department: [this.reservation!.department, []],
        jobTitle: [this.reservation!.job_title, []],
        surnameChinese: [this.reservation!.surname_chinese, []],
        nameChinese: [this.reservation!.name_chinese, []],
        familyName: [this.reservation!.family_name, []],
        firstName: [this.reservation!.first_name, []],
        birthday: [this.reservation!.birthday, []],
        gender: [this.reservation!.gender ? this.reservation!.gender : '', []],
        postalCode: [this.reservation!.postal_code, []],
        prefecture: [this.reservation!.prefecture, []],
        municipalities: [this.reservation!.municipalities, []],
        streetAddress: [this.reservation!.street_address, []],
        nameAndNumberBuilding: [this.reservation!.name_and_number_building, []],
        mobilePhoneNumber: [this.reservation!.mobile_phone_number, []],
        userRequest: [this.reservation!.user_request, []],
        memo: [this.reservation!.memo, []],
        mailSendStatus: [this.reservation!.mail_send_status, []],
      }, {
      // validator: this.isValidReservationDate
      validator: []
    }
    );
  }

  /**
   * 入力項目の作成
   */
  addOptionForm(questions: ReservationQuestion[]) {
    if (this.reservation) {
      //  設問分ループ
      for (let i = 0 ; i < questions.length ; i++) {
        // バリデーション設定
        const validators: ValidatorFn[] = this.reservationCheckService.createValidatorList(
          questions[i].question_type!,
          questions[i].question_check!,
          questions[i].question_check_range_lower!,
          questions[i].question_check_range_upper!,
          questions[i].question_check_col_count_lower!,
          questions[i].question_check_col_count_upper!,
        );
        // フォーム項目設定
        if (questions[i].question_type == 'string') {
          let defaultValue = '';
          if (this.reservation.answer) {
            for (const answerValue of this.reservation.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.formBuilder.group({name: questions[i].question_names[0].name, value: new FormControl(defaultValue, validators)}));

        } else if (questions[i].question_type == 'text-area') {
          let defaultValue = '';
          if (this.reservation.answer) {
            for (const answerValue of this.reservation.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.formBuilder.group({name: questions[i].question_names[0].name, value: new FormControl(defaultValue, validators)}));

        } else if (questions[i].question_type == 'number') {
          let defaultValue = '';
          if (this.reservation.answer) {
            for (const answerValue of this.reservation.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.formBuilder.group({name: questions[i].question_names[0].name, value: new FormControl(defaultValue, validators)}));

        } else if (questions[i].question_type == 'select') {
          let defaultValue = '';
          if (this.reservation.answer) {
            for (const answerValue of this.reservation.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.createSelectItemGroup(questions[i], defaultValue, validators));

        } else if (questions[i].question_type == 'checkbox') {
          const isRequired = questions[i].question_check?.some(valid => valid === AppConst.QUESTION_ITEM_CHECK_REQUIRED) ?? false; // 必須確認
          const checkItems: FormGroup[] = [];
          for (let opt of questions[i].question_select_items!) {
            let isSelected = false;
            if (this.reservation.answer) {
              for (const answerValue of this.reservation.answer) {
                if (questions[i].question_id === answerValue.id) {
                  for (const answer of answerValue.value) {
                    if (opt.value == answer) {
                      isSelected = true;
                    }
                  }
                }
              }
            }
            checkItems.push(this.formBuilder.group({name: opt.items[0].item, value: opt.value, selected: isSelected}));
          }
          this.options.push(this.createCheckItemGroup(questions[i], checkItems, isRequired));

        } else if (questions[i].question_type == 'radio') {
          const radioItems: FormGroup[] = [];
          for (let opt of questions[i].question_select_items!) {
            let isSelected = false;
            if (this.reservation.answer) {
              for (const answerValue of this.reservation.answer) {
                if (questions[i].question_id === answerValue.id) {
                  if (opt.value == answerValue.value) {
                    isSelected = true;
                  }
                }
              }
            }
            radioItems.push(this.formBuilder.group({name: opt.items[0].item, value: new FormControl(opt.value, validators), selected: isSelected}));
          }
          this.options.push(this.createRadioItemGroup(questions[i], radioItems));
        } else {
        }
      }
    }
  }

  /**
   * プルダウン項目に関連項目を含めてFormbuilderに追加する
   */
  private createSelectItemGroup(question: ReservationQuestion, answer: string, validators: ValidatorFn[]) {
    let itemGroup = this.formBuilder.group({});
    itemGroup.addControl('name', new FormControl(question.question_names[0].name));
    itemGroup.addControl('value', new FormControl(answer, validators));
    return itemGroup;
  }

  /**
   * チェックボックスを関連項目を含めてFormbuilderに追加する
   */
  private createCheckItemGroup(question: ReservationQuestion, checkGroup: FormGroup<any>[], isRequired: boolean) {
    let itemGroup = this.formBuilder.group({}, {validators: isRequired ? isAnyChecked() : []});
    itemGroup.addControl('name', new FormControl(question.question_names[0].name));
    itemGroup.addControl('value', new FormControl(checkGroup));
    return itemGroup;
  }

  /**
   * ラジオボタンを関連項目を含めてFormbuilderに追加する
   */
  private createRadioItemGroup(question: ReservationQuestion, radioGroup: FormGroup<any>[]) {
    let itemGroup = this.formBuilder.group({});
    itemGroup.addControl('name', new FormControl(question.question_names[0].name));
    itemGroup.addControl('value', new FormControl(radioGroup));
    return itemGroup;
  }

  /**
   * SelectのChangeイベント
   */
  selectChange(event: MatSelectChange, itemPosition: number) {
  }

  /**
   * CheckBoxのChangeイベント
   */
  checkToggle(event: MatCheckboxChange, itemPosition: number, checkPosition: number) {
    this.options.value[itemPosition].value[checkPosition].value.selected = event.checked;
  }

  /**
   * RadioのChangeイベント
   */
  radioChange(event: MatRadioChange, itemPosition: number) {
    for (let opt of this.options.value[itemPosition].value) {
      if (opt.value.value == event.source.value) {
        opt.value.selected = true;
      } else {
        opt.value.selected = false;
      }
    }
  }

  /**
   * 必須のラベル表示判定
   */
  isRequired(checks: string[]): boolean {
    if (checks.includes(AppConst.QUESTION_ITEM_CHECK_REQUIRED)) {
      return true;
    }
    return false;
  }

  //更新ボタン
  addReservation(): void {
    if (this.reservationFormGroup.valid) {
      let baseReservationStatus = this.reservation!.reservation_status;
      this.reservation!.reservation_status = this.reservationStatusControl.value;
      this.reservation!.email_address = this.emailAddressControl.value;
      this.reservation!.company_name = this.companyNameControl.value;
      this.reservation!.department = this.departmentControl.value;
      this.reservation!.job_title = this.jobTitleControl.value;
      this.reservation!.surname_chinese = this.surnameChineseControl.value;
      this.reservation!.name_chinese = this.nameChineseControl.value;
      this.reservation!.family_name = this.familyNameControl.value;
      this.reservation!.first_name = this.firstNameControl.value;
      this.reservation!.birthday = this.birthdayControl.value;
      this.reservation!.gender = this.genderControl.value;
      this.reservation!.postal_code = this.postalCodeControl.value;
      this.reservation!.prefecture = this.prefectureControl.value;
      this.reservation!.municipalities = this.municipalitiesControl.value;
      this.reservation!.street_address = this.streetAddressControl.value;
      this.reservation!.name_and_number_building = this.nameAndNumberBuildingControl.value;
      this.reservation!.mobile_phone_number = this.mobilePhoneNumberControl.value;
      this.reservation!.user_request = this.userRequestControl.value;
      this.reservation!.memo = this.memoControl.value;
      let notificationMode = AppConst.MAIL_NOTIFICATION_OFF;
      if (this.reservationStatusControl.value === AppConst.RESERVATION_STATUS_CANCEL) {
        this.reservation!.reservation_cancel_date = new Date();
        this.reservation!.reservation_cancel_user = 'admin';
        // 取消にする時
        if (baseReservationStatus !== AppConst.RESERVATION_STATUS_CANCEL) {
          notificationMode = AppConst.MAIL_NOTIFICATION_ON_CANCEL;
        }
      }
      this.reservation!.notification_mode = notificationMode;
      let answer = [];
      // カスタム項目がある場合
      if (this.questions && this.questions.length > 0) {
        for (let i=0; i<this.questions.length; i++) {
          if (this.questions[i].question_type == 'checkbox') {
            let checkboxValues = [];
            for (const value of this.options.value[i].value) {
              if (value.value.selected) {
                checkboxValues.push(value.value.value);
              }
            }
            answer.push({id:this.questions[i].question_id,value:checkboxValues});
          } else if (this.questions[i].question_type == 'radio') {
            for (const value of this.options.value[i].value) {
              if (value.value.selected) {
                answer.push({id:this.questions[i].question_id,value:value.value.value});
              }
            }
          } else {
            answer.push({id:this.questions[i].question_id,value:this.options.value[i].value});
          }
        }
      }
      this.reservation!.user_site_url = this.reservation?.user_site_url ? this.reservation?.user_site_url : '';
      this.reservation!.answer = answer;
      if (this.reservation) {
        let request: ApiSetReservationsRequest = {
          access_token: '',
          change_type: this.data.change_type,
          reservations: [this.reservation]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setReservations(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  clearBirthday(): void {
    this.birthdayControl.setValue(null);
  }

}
